import type { Mint } from "@solana/spl-token0.3.8";
import { TOKEN_PROGRAM_ID, unpackMint } from "@solana/spl-token0.3.8";
import type { AccountInfo, Connection } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import type {
  AccountParser,
  MintInfoMap,
  ParsedAccount,
  ParsedAccountBase,
  ParsedLocalTransaction,
} from "@stepfinance/common";
import { NATIVE_SOL, SYSTEM_PROGRAM } from "@stepfinance/common";
import type { TokenAccount } from "@stepfinance/tokens";
import { MintParser, TokenAccountParser } from "@stepfinance/tokens";

import { wrapNativeMintInfo } from "./accounts/native";
import { BaseError } from "./baseError";
import { EventEmitter } from "./eventEmitter";

const DEFAULT_CACHE_TIME_MS = 60 * 1000;

export const precacheUserTokenAccounts = async (connection: Connection, owner?: PublicKey) => {
  if (!owner) {
    return;
  }

  // user accounts are update via ws subscription
  const accounts = await connection.getTokenAccountsByOwner(owner, {
    programId: TOKEN_PROGRAM_ID,
  });

  const currentAccounts = Array.from(genericCache.values());
  const userAcc = currentAccounts.filter((a) => {
    if (a === undefined) {
      return false;
    }
    if (a.info && a.info.mint && a.info.mint.equals(NATIVE_SOL)) {
      return false;
    }
    return a.info && a.info.owner && a.info.owner.equals(owner);
  });

  userAcc.forEach((account) => {
    cache.delete(account.pubkey);
  });

  accounts.value.forEach((info) => {
    cache.add(info.pubkey, info.account, TokenAccountParser);
  });
};

export const getMintInfo = async (connection: Connection, pubKey: PublicKey) => {
  if (pubKey.equals(NATIVE_SOL)) {
    return wrapNativeMintInfo();
  }

  const info = await connection.getAccountInfo(pubKey);
  if (info === null) {
    throw new Error("Failed to find mint account");
  }

  return unpackMint(pubKey, info);
};

export class AccountNotFoundError extends BaseError {}

const pendingMintCalls = new Map<string, Promise<ParsedAccount<Mint>>>();
const pendingCalls = new Map<string, Promise<ParsedAccountBase>>();
const genericCache = new Map<string, ParsedAccountBase>();
const mintCache = new Map<string, ParsedAccount<Mint>>();
const transactionCache = new Map<string, ParsedLocalTransaction | null>();
const keyToAccountParser = new Map<string, AccountParser>();
const expiryTsMap = new Map<string, number>();

export const getCachedAccount = (predicate: (account: TokenAccount) => boolean) => {
  for (const account of genericCache.values()) {
    if (predicate(account)) {
      return account as TokenAccount;
    }
  }
};

const setExpiry = (address: string, cacheTime: number) => {
  const now = Date.now();
  expiryTsMap.set(address, now + cacheTime);
};

const getIsExpired = (address: string) => {
  const now = Date.now();
  const expiryTs = expiryTsMap.get(address);
  const isExpired = expiryTs && now > expiryTs;
  return Boolean(isExpired);
};

export const cache = {
  emitter: new EventEmitter(),
  query: async (
    connection: Connection,
    pubKey: string | PublicKey,
    parser?: AccountParser,
    cacheTime = DEFAULT_CACHE_TIME_MS,
  ) => {
    let id: PublicKey;
    if (typeof pubKey === "string") {
      id = new PublicKey(pubKey);
    } else {
      id = pubKey;
    }
    const address = id.toBase58();

    const account = genericCache.get(address);
    const isExpired = getIsExpired(address);

    if (account && !isExpired) {
      return account;
    }

    let query = pendingCalls.get(address);
    if (query) {
      return query;
    }

    query = connection.getAccountInfo(id).then((data) => {
      if (!data) {
        throw new AccountNotFoundError("Account not found with addresss ID " + id.toBase58());
      }

      setExpiry(address, cacheTime);

      return cache.add(id, data, parser);
    }) as Promise<TokenAccount>;
    pendingCalls.set(address, query as any);

    return query;
  },
  add: (
    id: PublicKey | string,
    obj: AccountInfo<Buffer>,
    parser?: AccountParser,
    cacheTime = DEFAULT_CACHE_TIME_MS,
  ) => {
    if (obj.data.length === 0) {
      return;
    }

    const address = typeof id === "string" ? id : id?.toBase58();
    if (!parser) {
      throw new Error("Parser needs to be registered or passed as a parameter");
    }

    pendingCalls.delete(address);
    const account = parser(new PublicKey(address), obj);
    if (!account) {
      return;
    }

    const isNew = !genericCache.has(address);
    setExpiry(address, cacheTime);
    genericCache.set(address, account);
    cache.emitter.raiseCacheUpdated(address, isNew, parser);
    return account;
  },
  has: (pubKey: string | PublicKey): boolean => {
    const key = pubKey instanceof PublicKey ? pubKey.toBase58() : pubKey;
    return genericCache.has(key);
  },
  get: (pubKey: string | PublicKey) => {
    let key: string;
    if (typeof pubKey !== "string") {
      key = pubKey.toBase58();
    } else {
      key = pubKey;
    }

    return genericCache.get(key);
  },
  getAll: () => {
    return genericCache;
  },
  delete: (pubKey: string | PublicKey) => {
    let key: string;
    if (typeof pubKey !== "string") {
      key = pubKey.toBase58();
    } else {
      key = pubKey;
    }

    if (genericCache.get(key)) {
      genericCache.delete(key);
      cache.emitter.raiseCacheDeleted(key);
      return true;
    }
    return false;
  },
  registerParser: (pubkey: PublicKey | string, parser: AccountParser) => {
    if (pubkey) {
      const address = typeof pubkey === "string" ? pubkey : pubkey?.toBase58();
      keyToAccountParser.set(address, parser);
    }

    return pubkey;
  },
  queryMint: async (
    connection: Connection,
    pubKey: string | PublicKey,
    cacheTime = DEFAULT_CACHE_TIME_MS,
  ): Promise<ParsedAccount<Mint>> => {
    let id: PublicKey;
    if (typeof pubKey === "string") {
      id = new PublicKey(pubKey);
    } else {
      id = pubKey;
    }

    const address = id.toBase58();

    const mint = mintCache.get(address);
    const isExpired = getIsExpired(address);

    if (mint && !isExpired) {
      return mint;
    }

    let query = pendingMintCalls.get(address);
    if (query) {
      return query;
    }

    query = connection.getAccountInfo(id).then((data) => {
      if (!data) {
        throw new AccountNotFoundError("Account not found with addresss ID " + id.toBase58());
      }

      return cache.addMint(id, data, cacheTime);
    }) as Promise<ParsedAccount<Mint>>;

    pendingMintCalls.set(address, query);

    return query;
  },
  getMint: (pubKey: string | PublicKey | undefined) => {
    if (!pubKey) {
      return;
    }

    let key: string;
    if (typeof pubKey !== "string") {
      key = pubKey.toBase58();
    } else {
      key = pubKey;
    }

    return mintCache.get(key)?.info;
  },
  getAllMints: () => {
    return mintCache;
  },
  hasMint: (pubKey: string | PublicKey | undefined): boolean => {
    if (!pubKey) {
      return false;
    }

    const key = pubKey instanceof PublicKey ? pubKey.toBase58() : pubKey;
    return mintCache.has(key);
  },
  addMint: (pubKey: PublicKey, obj: AccountInfo<Buffer>, cacheTime = DEFAULT_CACHE_TIME_MS) => {
    const mint = MintParser(pubKey, obj);
    const id = pubKey.toBase58();
    pendingMintCalls.delete(id);
    setExpiry(id, cacheTime);
    mintCache.set(id, mint);
    return mint;
  },
  addMintInfoMap: (mintInfoMap: MintInfoMap, cacheTime = DEFAULT_CACHE_TIME_MS) => {
    for (const [mintAddress, rawMintInfo] of Object.entries(mintInfoMap)) {
      const { freezeAuthority, mintAuthority, supply } = rawMintInfo;
      const mintInfo: Mint = {
        ...rawMintInfo,
        address: new PublicKey(mintAddress),
        freezeAuthority: freezeAuthority ? new PublicKey(freezeAuthority) : null,
        mintAuthority: mintAuthority ? new PublicKey(mintAuthority) : null,
        supply: BigInt(supply),
        tlvData: Buffer.alloc(0),
      };
      const parsedAccount: ParsedAccount<Mint> = {
        pubkey: new PublicKey(mintAddress),
        // throw in some dummy data to appease the type gods
        account: {
          executable: false,
          owner: SYSTEM_PROGRAM,
          lamports: 0,
          data: Buffer.alloc(0),
        },
        info: mintInfo,
      };
      setExpiry(mintAddress, cacheTime);
      mintCache.set(mintAddress, parsedAccount);
    }
  },
  addTransaction: (signature: string, tx: ParsedLocalTransaction | null) => {
    transactionCache.set(signature, tx);
    return tx;
  },
  addBulkTransactions: (txs: Array<ParsedLocalTransaction>) => {
    for (const tx of txs) {
      transactionCache.set(tx.signature.signature, tx);
    }
    return txs;
  },
  getTransaction: (signature: string) => {
    const transaction = transactionCache.get(signature);
    return transaction;
  },
  getAllTransactions: () => {
    return transactionCache;
  },
  clear: () => {
    genericCache.clear();
    transactionCache.clear();
    cache.emitter.raiseCacheCleared();
  },
};
